import { useCollection } from 'react-firebase-hooks/firestore';
import {
  collection,
  query,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import { createBatchedWriter, storagePathFromUrl } from '@/lib/cascade';
import { useAuth } from '../../auth/hooks/useAuth';
import type { Company, CompanyInput } from '@elka/types';

// Firestore `in` queries are capped at 30 values per query.
function chunkBy30<T>(values: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < values.length; i += 30) {
    out.push(values.slice(i, i + 30));
  }
  return out;
}

async function fetchIdsByField(
  collectionPath: string,
  field: string,
  value: string
): Promise<string[]> {
  const snap = await getDocs(
    query(collection(db, ...splitPath(collectionPath)), where(field, '==', value))
  );
  return snap.docs.map((d) => d.id);
}

async function fetchDocsByIn(
  collectionPath: string,
  field: string,
  ids: string[]
): Promise<{ id: string; data: Record<string, unknown> }[]> {
  if (ids.length === 0) return [];
  const out: { id: string; data: Record<string, unknown> }[] = [];
  for (const chunk of chunkBy30(ids)) {
    const snap = await getDocs(
      query(collection(db, ...splitPath(collectionPath)), where(field, 'in', chunk))
    );
    for (const d of snap.docs) out.push({ id: d.id, data: d.data() });
  }
  return out;
}

export function useCompanies() {
  const { user } = useAuth();

  // Query pattern same as EM's useClients - unconditional
  const companiesQuery = query(
    collection(db, ...splitPath(COLLECTIONS.crm.companies)),
    orderBy('createdAt', 'desc')
  );

  const [snapshot, loading, error] = useCollection(companiesQuery);

  const companies: Company[] =
    (snapshot?.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Company[]) || [];

  // Add company
  const addCompany = async (data: CompanyInput) => {
    if (!user) throw new Error('לא מחובר');

    const timestamp = serverTimestamp();
    const userInfo = {
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: timestamp,
    };

    const docRef = await addDoc(collection(db, ...splitPath(COLLECTIONS.crm.companies)), {
      ...data,
      ...userInfo,
    });

    return docRef.id;
  };

  // Update company. When the display name changes, fan the new value out to every
  // doc that denormalizes it (contacts/deals/activities/tasks) so list/detail views
  // don't keep showing the old name until each child is independently re-saved.
  const updateCompany = async (id: string, data: Partial<CompanyInput>) => {
    if (!user) throw new Error('לא מחובר');

    const existing = companies.find((c) => c.id === id);
    const renamed = !!(
      data.name !== undefined &&
      existing &&
      data.name &&
      existing.name !== data.name
    );

    const timestamp = serverTimestamp();
    const updatedByInfo = {
      updatedBy: user.displayName || user.email || 'Unknown',
      updatedAt: timestamp,
    };

    // ponytail: on a rename, fan out to the children BEFORE writing the parent's
    // new name. If any child read/write throws, the parent stays on the OLD name,
    // so `renamed` (computed against the live doc) still evaluates true on a retry
    // and the whole fan-out re-runs. Recovery guarantee: the rename is never left
    // half-applied in a way a retry can't finish. Ceiling: children are updated in
    // separate batches, so a partial-batch failure still needs a retry to converge.
    if (renamed) {
      const writer = createBatchedWriter();
      const newName = data.name!;
      const childFan = { companyName: newName, ...updatedByInfo };
      const childCollections: string[] = [
        COLLECTIONS.crm.contacts,
        COLLECTIONS.crm.deals,
        COLLECTIONS.crm.activities,
        COLLECTIONS.crm.tasks,
        // Quotes app denormalizes companyName too — keep it in sync so quote
        // PDFs don't render a stale company name after a rename.
        COLLECTIONS.quotes.quotes,
        COLLECTIONS.quotes.costEstimates,
        COLLECTIONS.quotes.bidRequests,
        COLLECTIONS.quotes.bills,
      ];
      for (const collectionPath of childCollections) {
        const docs = await getDocs(
          query(collection(db, ...splitPath(collectionPath)), where('companyId', '==', id))
        );
        for (const d of docs.docs) {
          // A quote that left 'draft' was already sent to the client — frozen, rename or not.
          if (collectionPath === COLLECTIONS.quotes.quotes && d.data().status !== 'draft') continue;
          writer.update(doc(db, ...splitPath(collectionPath), d.id), childFan);
        }
      }
      await writer.commit();
    }

    await updateDoc(doc(db, ...splitPath(COLLECTIONS.crm.companies), id), {
      ...data,
      ...updatedByInfo,
    });
  };

  // Delete company — cascade through every collection that references this companyId,
  // both directly (`companyId`) and transitively via contacts/deals (the activities,
  // tasks, deal comments, and deal documents that link through them). The UI promises
  // this cascade; without it deletes left orphaned data pointing at a missing company.
  const deleteCompany = async (id: string) => {
    if (!user) throw new Error('לא מחובר');

    // Direct children: ids of contacts and deals owned by this company.
    const contactIds = await fetchIdsByField(COLLECTIONS.crm.contacts, 'companyId', id);
    const dealIds = await fetchIdsByField(COLLECTIONS.crm.deals, 'companyId', id);

    // Activities and tasks may reference the company directly OR via contact/deal.
    // De-dupe across the three queries (the same doc could match multiple).
    const collectIds = async (collectionPath: string) => {
      const ids = new Set<string>();
      for (const d of await fetchDocsByIn(collectionPath, 'companyId', [id])) ids.add(d.id);
      for (const d of await fetchDocsByIn(collectionPath, 'contactId', contactIds)) ids.add(d.id);
      for (const d of await fetchDocsByIn(collectionPath, 'dealId', dealIds)) ids.add(d.id);
      return Array.from(ids);
    };
    const activityIds = await collectIds(COLLECTIONS.crm.activities);
    const taskIds = await collectIds(COLLECTIONS.crm.tasks);

    // Deal-scoped collections: comments and documents.
    const commentDocs = await fetchDocsByIn(COLLECTIONS.crm.dealComments, 'dealId', dealIds);
    const documentDocs = await fetchDocsByIn(COLLECTIONS.crm.dealDocuments, 'dealId', dealIds);

    const writer = createBatchedWriter();
    for (const cid of contactIds)
      writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.contacts), cid));
    for (const did of dealIds) writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.deals), did));
    for (const aid of activityIds)
      writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.activities), aid));
    for (const tid of taskIds) writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.tasks), tid));
    for (const c of commentDocs)
      writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.dealComments), c.id));
    for (const d of documentDocs)
      writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.dealDocuments), d.id));
    writer.delete(doc(db, ...splitPath(COLLECTIONS.crm.companies), id));
    await writer.commit();

    // Best-effort Storage cleanup for deal documents — Firestore is the source of truth,
    // so a missing file doesn't roll back the cascade.
    await Promise.all(
      documentDocs.map(async (d) => {
        const fileUrl = (d.data as { fileUrl?: string }).fileUrl;
        if (!fileUrl) return;
        const path = storagePathFromUrl(fileUrl);
        try {
          await deleteObject(storageRef(storage, path));
        } catch (err) {
          console.warn('[deleteCompany] failed to delete storage file:', path, err);
        }
      })
    );
  };

  return {
    companies,
    isLoading: loading,
    error: error?.message || null,
    addCompany,
    updateCompany,
    deleteCompany,
  };
}
